import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CalendarRange, Loader2, RefreshCw, TrendingUp, Wallet } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchPeriods, type PeriodRow } from "@/lib/periods-api";
import { fetchRatesByPeriod } from "@/lib/rate-api";
import { summarizeRates, type RatesSummary } from "@/lib/rate-row-helpers";
import { getAccessToken } from "@/lib/local-auth";
import { formatDateDmyFromYmd, formatIDR } from "@/lib/format";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

const PERIOD_SUMMARY_LIMIT = 12;

function compareYmd(a: string, b: string): number {
  return new Date(`${a}T00:00:00Z`).getTime() - new Date(`${b}T00:00:00Z`).getTime();
}

function monthTitleFromEndDate(endDate: string): string {
  const d = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return endDate;
  const label = d.toLocaleString("id-ID", { month: "long", year: "numeric" });
  return label.length ? label[0].toUpperCase() + label.slice(1) : label;
}

function formatRangeId(p: Pick<PeriodRow, "start_date" | "end_date">) {
  return `${formatDateDmyFromYmd(p.start_date)} → ${formatDateDmyFromYmd(p.end_date)}`;
}

const STATUS_LABEL: Record<PeriodRow["status"], string> = {
  draft: "draft",
  open: "open",
  locked: "locked",
  closed: "closed",
  paid: "paid",
};

type PeriodSummaryRow = {
  period: PeriodRow;
  summary: RatesSummary;
};

function Dashboard() {
  const [rows, setRows] = useState<PeriodSummaryRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!getAccessToken()) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const list = await fetchPeriods();
      const sorted = [...list].sort((a, b) => compareYmd(b.start_date, a.start_date));
      const top = sorted.slice(0, PERIOD_SUMMARY_LIMIT);
      const withRates = await Promise.all(
        top.map(async (p) => {
          const rates = await fetchRatesByPeriod(p.id);
          return { period: p, summary: summarizeRates(rates) };
        }),
      );
      setRows(withRates);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal memuat ringkasan periode");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const aggregates = useMemo(() => {
    if (rows.length === 0) {
      return { totalGrand: 0, avgActivity: 0 };
    }
    const totalGrand = rows.reduce((a, r) => a + r.summary.grandEarn, 0);
    const withData = rows.filter((r) => r.summary.totalEmployees > 0);
    const avgActivity =
      withData.length > 0 ? withData.reduce((a, r) => a + r.summary.activityPct, 0) / withData.length : 0;
    return { totalGrand, avgActivity };
  }, [rows]);

  const hasToken = !!getAccessToken();

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">Dashboard</h1>
          <p className="mt-1.5 max-w-2xl text-xs leading-snug text-muted-foreground sm:text-sm">
            Ringkasan penggajian untuk hingga {PERIOD_SUMMARY_LIMIT} periode terbaru (berdasarkan data rate di server). Angka mengikuti
            perhitungan yang sama dengan halaman detail periode: biaya Hivedesk, jam tambahan, reimburse, insentif, dan cuti berbayar.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading || !hasToken} className="shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Muat ulang
        </Button>
      </div>

      {!hasToken ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Login terlebih dahulu untuk memuat ringkasan periode dari server.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Periode ditampilkan</div>
                    <div className="text-lg sm:text-xl font-semibold leading-tight mt-1.5 tabular-nums">{rows.length}</div>
                  </div>
                  <div className="h-8 w-8 rounded-md grid place-items-center bg-secondary text-primary shrink-0">
                    <CalendarRange className="h-4 w-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Jumlah grand total (semua baris)</div>
                    <div className="text-lg sm:text-xl font-semibold leading-tight mt-1.5 truncate">{formatIDR(aggregates.totalGrand)}</div>
                  </div>
                  <div className="h-8 w-8 rounded-md grid place-items-center bg-muted text-primary shrink-0">
                    <Wallet className="h-4 w-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground">Rata-rata aktivitas agregat</div>
                    <div className="text-lg sm:text-xl font-semibold leading-tight mt-1.5 tabular-nums">
                      {rows.length === 0 ? "—" : `${aggregates.avgActivity.toFixed(0)}%`}
                    </div>
                  </div>
                  <div className="h-8 w-8 rounded-md grid place-items-center bg-secondary text-primary shrink-0">
                    <TrendingUp className="h-4 w-4" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
              <CardTitle className="text-sm sm:text-base">Ringkasan per bulan</CardTitle>
              <Link
                to="/periode"
                className="text-sm text-primary hover:underline inline-flex items-center gap-1 py-2 min-h-[44px] sm:min-h-0 sm:py-0"
              >
                Kelola periode <ArrowRight className="h-3.5 w-3.5 sm:h-3 sm:w-3" />
              </Link>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-2 sm:py-3 font-medium">Periode</th>
                      <th className="py-2 sm:py-3 font-medium">Status</th>
                      <th className="py-2 sm:py-3 font-medium text-right">Karyawan</th>
                      <th className="py-2 sm:py-3 font-medium text-right">Jam tercatat</th>
                      <th className="py-2 sm:py-3 font-medium text-right">Aktivitas</th>
                      <th className="py-2 sm:py-3 font-medium text-right">Grand total</th>
                      <th className="py-2 sm:py-3 font-medium text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-muted-foreground">
                          <Loader2 className="h-5 w-5 animate-spin inline-block mr-2 align-middle" />
                          Memuat ringkasan…
                        </td>
                      </tr>
                    ) : null}
                    {!loading && rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-10 text-center text-muted-foreground">
                          Belum ada periode penggajian. Buat periode baru dari menu Periode Penggajian.
                        </td>
                      </tr>
                    ) : null}
                    {rows.map(({ period: p, summary: s }) => (
                      <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                        <td className="py-2 sm:py-3">
                          <div className="font-medium">{monthTitleFromEndDate(p.end_date)}</div>
                          <div className="text-xs text-muted-foreground">{formatRangeId(p)}</div>
                        </td>
                        <td className="py-2 sm:py-3">
                          <Badge variant="outline" className="font-normal">
                            {STATUS_LABEL[p.status]}
                          </Badge>
                        </td>
                        <td className="py-2 sm:py-3 text-right tabular-nums">{s.totalEmployees}</td>
                        <td className="py-2 sm:py-3 text-right tabular-nums font-mono text-xs sm:text-sm">{s.loggedTime}</td>
                        <td className="py-2 sm:py-3 text-right tabular-nums">{s.activityPct}%</td>
                        <td className="py-2 sm:py-3 text-right tabular-nums font-medium">{formatIDR(s.grandEarn)}</td>
                        <td className="py-2 sm:py-3 text-right">
                          <Button variant="outline" size="sm" asChild>
                            <Link to="/periode/$periodId" params={{ periodId: p.id }}>
                              Detail
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
